import React from 'react';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useState } from 'react';

const FAQPage = () => {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const faqData = [
    {
      category: "Getting Started",
      questions: [
        {
          question: "What is Postgame Buzz?",
          answer: "Postgame Buzz is an AI-powered platform that transforms your game reports and box scores into engaging sports podcasts. Simply upload your PDF game report, and our AI creates a professional podcast complete with commentary, analysis, and highlights."
        },
        {
          question: "How do I create my first podcast?",
          answer: "1. Sign up and log in to your account\n2. Select your team from our database\n3. Either upload a PDF game report OR type in your own game description\n4. Our AI processes the information and generates your podcast\n5. Listen, share, or download your custom sports podcast!"
        },
        {
          question: "What file formats do you accept?",
          answer: "We accept PDF files exported from sports management platforms like GameChanger. These should be text-based PDFs (not scanned images). Alternatively, you can simply type in your own description of what happened in the game - no PDF required! Just write a few paragraphs about the key plays, player performances, and game highlights."
        }
      ]
    },
    {
      category: "Credits & Billing",
      questions: [
        {
          question: "How does the credit system work?",
          answer: "Each podcast generation costs 1 credit. You can purchase credits through our secure payment system powered by Lemon Squeezy. Credits never expire and can be used anytime."
        },
        {
          question: "How much does it cost to create a podcast?",
          answer: "Podcast generation costs 1 credit per podcast. You can purchase credits in various packages - check our pricing page for current rates and bundle options."
        },
        {
          question: "What payment methods do you accept?",
          answer: "We accept all major credit cards, PayPal, and other payment methods through our secure payment processor, Lemon Squeezy. All transactions are encrypted and PCI compliant."
        },
      ]
    },
    {
      category: "Podcast Creation",
      questions: [
        {
          question: "How long does it take to generate a podcast?",
          answer: "Podcast generation typically takes 5-10 minutes, but can take up to 20 minutes during peak usage. You'll see a progress bar and can navigate away - we'll save your podcast to 'My Podcasts' when it's ready."
        },
        {
          question: "What information do I need to provide?",
          answer: "You'll need to select your team and then either upload a PDF game report OR type in your own game summary. If uploading a PDF, it should contain player stats, game scores, and other relevant game data. If typing manually, include key plays, player performances, final score, and any highlights you want featured in the podcast."
        },
        {
          question: "Can I customize the podcast content?",
          answer: "Currently, our AI automatically generates content based on your game data and team information. We're working on customization features for future releases."
        },
        {
          question: "How do I add my team?",
          answer: "You can easily add your team in the 'My Teams' section of your account. This is a required step that allows our AI to know what sport you're playing and use the full names of your players in the podcast. Simply provide your team name, sport, and player roster with their full names."
        }
      ]
    },
    {
      category: "Sharing & Downloads",
      questions: [
        {
          question: "Can I share my podcasts?",
          answer: "Yes! You can make podcasts public and share them via social media, direct links, or embed them on websites. Public podcasts get a shareable URL that works on all platforms."
        },
        {
          question: "Can I download my podcasts?",
          answer: "Absolutely! You can download your podcasts as MP3 files for offline listening or to upload to other platforms. Both audio and video formats are available."
        },
        {
          question: "Are there video versions of podcasts?",
          answer: "Yes! Every podcast automatically includes a video version with background visuals. You can stream or download both audio and video formats."
        }
      ]
    },
    {
      category: "Technical Support",
      questions: [
        {
          question: "My PDF upload failed. What should I do?",
          answer: "Ensure your PDF is text-based (not a scanned image) and contains game data. If you're exporting from GameChanger, make sure to export as PDF, not screenshot. If issues persist, contact our support team."
        },
        {
          question: "The podcast generation is taking too long. Is something wrong?",
          answer: "Generation can take up to 20 minutes during peak times. If it's been longer than 20 minutes, please refresh the page and check 'My Podcasts' - it may have completed. Contact support if the issue continues."
        },
        {
          question: "Can I use this on mobile devices?",
          answer: "Yes! Postgame Buzz works on all devices including smartphones and tablets. The interface is fully responsive and optimized for mobile use."
        },
      ]
    },
    {
      category: "Privacy & Data",
      questions: [
        {
          question: "Is my data secure?",
          answer: "Yes! We use industry-standard encryption, secure hosting, and follow best practices for data protection. Your uploaded files and generated content are stored securely and only accessible to you."
        },
        {
          question: "Do you store my uploaded files?",
          answer: "We temporarily store uploaded PDFs for processing, then securely delete them. Your generated podcasts are stored in your account for access and download."
        },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about creating AI-powered sports podcasts with Postgame Buzz
            </p>
          </div>

          {/* FAQ Content */}
          <div className="space-y-8">
            {faqData.map((category, categoryIndex) => (
              <div key={categoryIndex} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <div className="bg-blue-600 text-white px-6 py-4">
                  <h2 className="text-2xl font-semibold">{category.category}</h2>
                </div>
                <div className="divide-y divide-gray-200">
                  {category.questions.map((item, itemIndex) => {
                    const globalIndex = `${categoryIndex}-${itemIndex}`;
                    const isOpen = openItems[globalIndex];
                    
                    return (
                      <div key={itemIndex} className="border-b border-gray-200 last:border-b-0">
                        <button
                          className="w-full px-6 py-4 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200"
                          onClick={() => toggleItem(globalIndex)}
                        >
                          <div className="flex justify-between items-center">
                            <h3 className="text-lg font-medium text-gray-900 pr-4">
                              {item.question}
                            </h3>
                            {isOpen ? (
                              <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
                            )}
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                              {item.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
};

export default FAQPage;
